import { useState } from 'react';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { Button } from './button';
import { Dialog } from './dialog';
import { useToast } from './toast';

/**
 * One-time secret display. Server-side rotation endpoints return a plaintext
 * key exactly once and persist only its hash, so this dialog is the single
 * chance the user has to copy it — the copy warns as much.
 *
 * Mirrors the reveal treatment in the client vault tab; extracted here because
 * site keys and form secrets both need it.
 */
export function SecretRevealDialog({
  open,
  onClose,
  title,
  label,
  secret,
  description,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Field caption, e.g. "Site key". Also used in the copied-toast copy. */
  label: string;
  secret: string;
  description?: string;
}) {
  const [show, setShow] = useState(false);
  const toast = useToast();

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="space-y-3">
        {description ? (
          <p className="text-sm text-[var(--color-foreground-muted)]">{description}</p>
        ) : null}

        <div>
          <div className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 break-all rounded-md border border-border bg-muted/30 px-3 py-2 font-mono text-sm">
              {show ? secret : '•'.repeat(Math.min(secret.length, 24))}
            </div>
            <Button
              variant="secondary"
              onClick={() => setShow((v) => !v)}
              aria-label={show ? 'Hide' : 'Show'}
            >
              {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                void navigator.clipboard.writeText(secret);
                toast.success(`${label} copied`);
              }}
              aria-label={`Copy ${label}`}
            >
              <Copy className="size-4" />
            </Button>
          </div>
        </div>

        <p className="text-xs text-[var(--color-foreground-muted)]">
          Copy this now. It is stored hashed, so it cannot be shown again — you would have to
          generate a new one.
        </p>
      </div>

      <div className="mt-4 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </div>
    </Dialog>
  );
}
